import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/services/apiClient'
import { 
  ArrowLeftIcon, 
  EnvelopeIcon, 
  UserIcon, 
  LockClosedIcon,
  ShieldCheckIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'

const REGIONS = [
  '华东', '华南', '华北', '华中', '西南', '西北', '东北', '港澳台', '海外'
]

const ROLE_INFO = {
  admin: { label: '超级管理员', description: '拥有所有权限，可以管理系统所有功能', icon: '👑' },
  finance: { label: '财务管理员', description: '可以审批提现、查看报表', icon: '💰' },
  audit: { label: '审计管理员', description: '只能查看数据和报表', icon: '📊' },
  operator: { label: '运营商', description: '只能查看和管理自己的用户及佣金', icon: '🏢' }
}

export default function CreateUserPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    role: 'operator' as 'admin' | 'finance' | 'audit' | 'operator',
    region: '',
    commission_rate: '0.5'
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.email || !formData.full_name || !formData.password) {
      setError('请填写所有必填字段')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致')
      return
    }

    if (formData.password.length < 6) {
      setError('密码长度至少为6位')
      return
    }

    if (formData.role === 'operator' && !formData.commission_rate) {
      setError('运营商角色必须设置佣金比例')
      return
    }

    try {
      setIsLoading(true)

      const payload: any = {
        email: formData.email,
        full_name: formData.full_name,
        password: formData.password,
        role: formData.role
      }

      if (formData.role === 'operator') {
        payload.commission_rate = parseFloat(formData.commission_rate)
        if (formData.region) {
          payload.region = formData.region
        }
      }

      const response = await apiClient.createAdminUser(payload)

      if (response.success) {
        setSuccess(true)
        setTimeout(() => {
          navigate('/operators')
        }, 2000)
      } else {
        setError(response.message || '创建用户失败')
      }
    } catch (err: any) {
      setError(err.message || '创建用户失败')
      console.error('Failed to create user:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const currentRoleInfo = ROLE_INFO[formData.role]

  return (
    <div className="page-content">
      <div className="mb-8">
        <button
          onClick={() => navigate('/operators')}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-4 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          返回后台用户列表
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center text-white text-2xl shadow-lg">
            👤
          </div>
          <div>
            <h1 className="page-title">创建后台用户</h1>
            <p className="page-subtitle">创建新的管理员或运营商用户</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit} className="space-y-8">
                {error && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg flex items-start gap-3">
                    <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">创建失败</h3>
                      <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                  </div>
                )}

                {success && (
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg flex items-start gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-green-800">创建成功</h3>
                      <p className="text-sm text-green-700 mt-1">用户已创建，正在跳转...</p>
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <UserIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">基本信息</h3>
                  </div>
                  
                  <div className="space-y-4 pl-10">
                    <div>
                      <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="full_name"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          required
                          className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                          placeholder="请输入姓名"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        邮箱 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                          placeholder="user@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                          密码 <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <LockClosedIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            minLength={6}
                            className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="至少6位字符"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                          确认密码 <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <LockClosedIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            minLength={6}
                            className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="再次输入密码"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <ShieldCheckIcon className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">角色信息</h3>
                  </div>
                  
                  <div className="space-y-4 pl-10">
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                        角色 <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                        className="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      >
                        {Object.entries(ROLE_INFO).map(([key, info]) => (
                          <option key={key} value={key}>
                            {info.icon} {info.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {formData.role === 'operator' && (
                      <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800 mb-2">
                          <span className="text-lg">🏢</span>
                          <span className="font-medium">运营商专属设置</span>
                        </div>

                        <div>
                          <label htmlFor="commission_rate" className="block text-sm font-medium text-gray-700 mb-2">
                            佣金比例 <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="number"
                              id="commission_rate"
                              name="commission_rate"
                              value={formData.commission_rate}
                              onChange={handleChange}
                              required
                              min="0"
                              max="1"
                              step="0.01"
                              className="pl-10 pr-16 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                              placeholder="0.5"
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <span className="text-gray-500 text-sm font-medium bg-gray-100 px-2 py-1 rounded">
                                {(parseFloat(formData.commission_rate || '0') * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            💡 输入0到1之间的数值，例如0.5表示50%佣金
                          </p>
                        </div>

                        <div>
                          <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-2">
                            所属区域
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <MapPinIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <select
                              id="region"
                              name="region"
                              value={formData.region}
                              onChange={handleChange}
                              className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                            >
                              <option value="">请选择区域（可选）</option>
                              {REGIONS.map(region => (
                                <option key={region} value={region}>{region}</option>
                              ))}
                            </select>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            🌏 设置运营商的区域，可作为区域代理
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/operators')}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                    disabled={isLoading}
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 font-medium shadow-lg shadow-primary-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    disabled={isLoading || success}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        创建中...
                      </>
                    ) : success ? (
                      <>
                        <CheckCircleIcon className="w-5 h-5" />
                        创建成功
                      </>
                    ) : (
                      <>
                        <UserIcon className="w-5 h-5" />
                        创建用户
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="card sticky top-6">
            <div className="card-body">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">当前角色</h3>
              
              <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-xl mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{currentRoleInfo.icon}</span>
                  <div>
                    <h4 className="font-semibold text-green-900">
                      {currentRoleInfo.label}
                    </h4>
                  </div>
                </div>
                <p className="text-sm text-green-700">
                  {currentRoleInfo.description}
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">所有角色</h4>
                {Object.entries(ROLE_INFO).map(([key, info]) => (
                  <div
                    key={key}
                    className={`p-3 rounded-lg border transition-all cursor-pointer ${
                      formData.role === key
                        ? 'bg-primary-50 border-primary-300 shadow-sm'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, role: key as any }))}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{info.icon}</span>
                      <span className="text-sm font-medium text-gray-900">{info.label}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 text-lg">💡</span>
                  <div className="text-xs text-blue-700">
                    <p className="font-medium mb-1">提示</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>密码至少6位字符</li>
                      <li>邮箱将用于登录</li>
                      <li>运营商需设置佣金比例</li>
                      <li>区域设置可选</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
